import React, { useEffect, useState } from "react";
import axios from "axios";
import Spinner from "react-bootstrap/Spinner";
import config from "../../Config";
import PrintHeader from "../General/PrintHeader";
import Page from "../General/Page";

function truncateSubjectName(name, maxLength = 4) {
  if (!name) return "";
  const trimmed = String(name).trim();
  return trimmed.length <= maxLength ? trimmed : trimmed.slice(0, maxLength);
}

function groupColumnsByExam(columns) {
  const groups = [];
  (columns || []).forEach((col) => {
    const examId = col.Source_Exam_ID || 0;
    let group = groups.find((g) => g.Source_Exam_ID === examId);
    if (!group) {
      group = {
        Source_Exam_ID: examId,
        Exam_Title: col.Exam_Title || `Exam ${examId}`,
        Subjects: [],
      };
      groups.push(group);
    }
    group.Subjects.push(col);
  });
  return groups;
}

function formatMark(value) {
  if (value === null || value === undefined || value === "") return "-";
  const num = Number(value);
  if (Number.isNaN(num)) return "-";
  return Number.isInteger(num) ? String(num) : num.toFixed(2);
}

function formatPercent(value) {
  if (value === null || value === undefined || value === "") return "-";
  return Number(value).toFixed(2);
}

function isWeightedCalculation(preview) {
  return (preview?.Calculation_Method || "").toLowerCase() === "weightedpercent";
}

function PrintMasterSheet() {
  const [AccessKey] = useState(localStorage.getItem("AccessKey"));
  const [UserID] = useState(localStorage.getItem("ID"));
  const [MemberType] = useState(localStorage.getItem("MemberType"));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const mode = (urlParams.get("View") || "Combined").toLowerCase();
    const isSeparate = mode === "separate";

    document.title = isSeparate
      ? "Master Sheet (Separate Marks)"
      : "Composite Master Sheet";

    const data = {
      AccessKey,
      UserID,
      MemberType: MemberType || "Admin",
      ID: parseInt(urlParams.get("ID"), 10) || 0,
      Wing_Id: parseInt(urlParams.get("WingId"), 10) || 0,
      Class_Id: parseInt(urlParams.get("ClassId"), 10) || 0,
      Section_Id: parseInt(urlParams.get("SectionId"), 10) || 0,
      PrintTotal: isSeparate ? "Separate" : "Combined",
    };

    axios({
      method: "post",
      url: config.base_url + "ResultCard/GetMasterSheetPreview",
      headers: { "Content-Type": "application/json" },
      data,
    })
      .then((response) => {
        if (response.data.status_code === 1) {
          setPreview(
            response.data.Preview || {
              ResultCard: response.data.ResultCard,
              Sections: response.data.Sections,
              Warnings: response.data.Warnings,
              Total_Missing_Marks: response.data.Total_Missing_Marks,
              Mark_Display_Mode: isSeparate ? "Separate" : "Combined",
            }
          );
        } else {
          setError(response.data.status_message || "Unable to load master sheet.");
        }
        setLoading(false);
      })
      .catch(() => {
        setError("Network error while loading master sheet.");
        setLoading(false);
      });
  }, [AccessKey, UserID, MemberType]);

  const renderSignatureRow = () => (
    <table className="table mt-3">
      <tbody>
        <tr>
          <td style={{ width: "33%", textAlign: "center" }}>
            <div style={{ width: "200px", margin: "0 auto 12px", borderBottom: "1px solid" }} />
            Class Teacher
          </td>
          <td style={{ width: "33%", textAlign: "center" }}>
            <div style={{ width: "200px", margin: "0 auto 12px", borderBottom: "1px solid" }} />
            Controller Of Examination
          </td>
          <td style={{ width: "33%", textAlign: "center" }}>
            <div style={{ width: "200px", margin: "0 auto 12px", borderBottom: "1px solid" }} />
            Principal
          </td>
        </tr>
      </tbody>
    </table>
  );

  if (loading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" role="status" />
      </div>
    );
  }

  if (error) {
    return <div className="alert alert-danger m-4">{error}</div>;
  }

  const card = preview?.ResultCard;
  const sections = preview?.Sections || [];
  const isSeparate = (preview?.Mark_Display_Mode || "").toLowerCase() === "separate";
  const weighted = isWeightedCalculation(preview);
  const components = (card?.Components || []).filter((c) => c.Include_In_Total);
  const percentHeader = weighted ? "Wtd %" : "%";

  return (
    <Page orientation="landscape" size="a4">
      <table className="table table-sm table-bordered table-print" style={{ border: "transparent" }}>
        <thead>
          <tr>
            <th>
              <PrintHeader title="" />
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              {preview?.Calculation_Note && (
                <p className="small text-muted mb-3">{preview.Calculation_Note}</p>
              )}

              {preview?.Warnings?.length > 0 && (
                <div className="alert alert-warning py-2 small mb-3">
                  {preview.Warnings.map((w, i) => (
                    <div key={i}>{w}</div>
                  ))}
                </div>
              )}

              {sections.length === 0 ? (
                <p className="text-muted">No sections found for the selected filters.</p>
              ) : (
                sections.map((section, sIdx) => {
                  const subjectColumns =
                    section.Subject_Columns?.length > 0
                      ? section.Subject_Columns
                      : section.Students?.[0]?.Subjects || [];
                  const examGroups = isSeparate ? groupColumnsByExam(subjectColumns) : [];
                  const subjectMaxTotal =
                    section.Subject_Max_Total ??
                    section.Section_Total_Marks ??
                    subjectColumns.reduce((sum, col) => sum + (Number(col.Total_Marks) || 0), 0);

                  return (
                    <div
                      key={section.Section_ID || sIdx}
                      style={{
                        pageBreakAfter: sIdx !== sections.length - 1 ? "always" : "auto",
                      }}
                    >
                      <div className="row mb-2">
                        <div className="col-md-4">
                          <h6 style={{ fontSize: "12px" }}>
                            Master Sheet: {section.Wing_Name}-{section.Class_Name}-
                            {section.Section_Name}
                          </h6>
                        </div>
                        <div className="col-md-4 text-center">
                          <h6 style={{ fontSize: "12px" }}>
                            {card?.Print_Title || card?.Title} ({card?.ResultCard_Code})
                          </h6>
                          <small>
                            Total Marks: {formatMark(subjectMaxTotal)}
                            {components.length > 1 && (
                              <>
                                {" "}
                                &middot; {components.length} source exam
                                {components.length > 1 ? "s" : ""}
                              </>
                            )}
                          </small>
                        </div>
                        <div className="col-md-4 text-end">
                          <h6 style={{ fontSize: "12px" }}>{section.Section_Teacher_Name}</h6>
                        </div>
                      </div>

                      {components.length > 1 && (
                        <p className="small mb-2">
                          {components.map((comp, i) => (
                            <span key={comp.Source_Exam_ID || i}>
                              {i > 0 ? " + " : ""}
                              {comp.Exam_Print_Title || comp.Exam_Name || comp.Display_Label}
                              {comp.Weightage_Percent ? ` (${comp.Weightage_Percent}%)` : ""}
                            </span>
                          ))}
                        </p>
                      )}

                      <table className="table table-sm table-bordered table-print">
                        <thead>
                          {isSeparate && examGroups.length > 0 && (
                            <tr>
                              <th colSpan={3} style={{ verticalAlign: "bottom" }} />
                              {examGroups.map((group) => (
                                <th
                                  key={group.Source_Exam_ID}
                                  colSpan={group.Subjects.length}
                                  style={{ textAlign: "center", fontSize: "11px" }}
                                >
                                  {group.Exam_Title}
                                </th>
                              ))}
                              <th colSpan={4} />
                            </tr>
                          )}
                          <tr>
                            <th style={{ width: "40px", verticalAlign: "top" }}>Sr</th>
                            <th style={{ width: "90px", verticalAlign: "top" }}>Reg No</th>
                            <th style={{ minWidth: "160px", verticalAlign: "top" }}>Student Name</th>
                            {subjectColumns.map((sub, i) => (
                              <th
                                key={i}
                                title={sub.Subject_Name}
                                style={{
                                  textAlign: "center",
                                  fontSize: "11px",
                                  maxWidth: "36px",
                                  verticalAlign: "top",
                                }}
                              >
                                {truncateSubjectName(sub.Subject_Name)}
                                <br />
                                <small>{formatMark(sub.Total_Marks)}</small>
                              </th>
                            ))}
                            <th
                              style={{
                                verticalAlign: "top",
                                textAlign: "center",
                                fontSize: "11px",
                              }}
                            >
                              {formatMark(subjectMaxTotal)}
                              <br />
                              Total Marks
                            </th>
                            <th style={{ verticalAlign: "top" }}>{percentHeader}</th>
                            <th style={{ verticalAlign: "top" }}>Grade</th>
                            <th style={{ verticalAlign: "top" }}>Position</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(section.Students || []).map((student) => {
                            const totalObtained =
                              student.Subject_Obtained_Sum ??
                              (student.Subjects || []).reduce(
                                (sum, sub) =>
                                  sum + (sub.Obtained_Marks != null ? Number(sub.Obtained_Marks) : 0),
                                0
                              );

                            return (
                              <tr
                                key={student.Student_ID}
                                className={
                                  student.Missing_Marks_Count > 0 ? "table-warning" : ""
                                }
                              >
                                <td className="text-center">{student.Serial_No}</td>
                                <td>{student.Registration_No}</td>
                                <td>{student.Student_Name}</td>
                                {(student.Subjects || []).map((sub, i) => (
                                  <td key={i} className="text-center">
                                    {formatMark(sub.Obtained_Marks)}
                                  </td>
                                ))}
                                <td className="text-center">{formatMark(totalObtained)}</td>
                                <td className="text-center">{formatPercent(student.Percent_Marks)}</td>
                                <td className="text-center">{student.Grade || "-"}</td>
                                <td className="text-center">{student.Section_Position || "-"}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>

                      {renderSignatureRow()}
                    </div>
                  );
                })
              )}
            </td>
          </tr>
        </tbody>
      </table>
    </Page>
  );
}

export default PrintMasterSheet;
